import { Stack, Divider, Link } from '@chakra-ui/react';
import {
	app,
	environment as envLabels,
	environments as environmentsLabels,
} from '../../../../@xmcloud/core/messages/en';
import {
	HeadingMd,
	Icon,
	IconText,
	LoadingSkeleton,
	TextBox,
} from '../../../shared-components';
import { mdiSourceBranch, mdiOpenInNew } from '@mdi/js';
import { t } from '@transifex/native';
import EdgeToken from '../components/EdgeToken';
import {
	useEnvironmentDetails,
	useProjectDetails,
} from '../../../../@xmcloud/hooks';
import { ApiTokenComponent } from '../components/EdgeAPIToken';
import { ContextID } from '../components/ContextID';
import {
	getReadableDate,
	getReadableTime,
} from '../../../../@xmcloud/utils/dateUtils';
import { CombinedEnvironmentDetails } from '../../create-edit-environment/legacy-combined-env/CombinedEnvironmentDetails';
import { useGetEnvType } from '../hooks/useGetEnvType';

const {
	hostNamesAndUrls: hostNamesAndUrlstxt,
	hostName: hostNametxt,
	dashboard: dashboardtxt,
	authoringGraphQlEndpoint: authoringGraphQlEndpointtxt,
	authoringGraphQlEndpointTooltip,
	launchIDE: launchIDEtxt,
} = envLabels;
const { autoDeploy: autoDeploytxt } = environmentsLabels;
const { details: detailstxt } = app;

export const EnvironmentDetailsLatest: React.FC = () => {
	const { projectId, environmentId, isEhEnv } = useGetEnvType();

	const {
		environment,
		isLoading: isEnvironmentLoading,
		cmEnvironmentId,
	} = useEnvironmentDetails(environmentId!);

	const { environment: linkedCmEnvironment } = useEnvironmentDetails(
		cmEnvironmentId!,
		isEhEnv && !!cmEnvironmentId,
	);

	const { region, isLoading: projectIsLoading } = useProjectDetails(
		projectId!,
	);

	const isLoading = projectIsLoading || isEnvironmentLoading;

	const host = environment?.host || '';

	if (isLoading) return <LoadingSkeleton height={36} amount={12} mt={10} />;

	const authoringGraphQlEndpoint = `https://${host}/sitecore/api/authoring/graphql/playground`;
	const dashboardUrl = `https://${host}/sitecore`;

	const getContextIdProps = () => ({
		previewContextId: environment?.previewContextId!,
		liveContextId: environment?.liveContextId!,
		environmentId: environmentId!,
	});

	const CmView = () => (
		<Stack spacing={5} mt={5}>
			<HeadingMd text={detailstxt} />
			<ContextID {...getContextIdProps()} />
			<TextBox title="Environment ID" subTitle={environment?.id!} />
			<TextBox title="Type" subTitle={environment?.tenantType} />
			<TextBox title="Selected region" subTitle={region} />
			<TextBox
				title="Base image version"
				subTitle={`${environment?.sitecoreMajorVersion}.${environment?.sitecoreMinorVersion}`}
			/>
			<Divider />
		</Stack>
	);

	const EhView = () => (
		<Stack spacing={5} mt={5}>
			<HeadingMd text={detailstxt} />
			<TextBox
				title="Linked authoring environment"
				subTitle={linkedCmEnvironment?.name!}
				isLink={!!linkedCmEnvironment?.name}
				href={`/projects/${projectId}/cm-environments/${linkedCmEnvironment?.id!}/details`}
			/>
			<TextBox
				title="Linked authoring environment ID"
				subTitle={linkedCmEnvironment?.id!}
			/>
			<Divider />
		</Stack>
	);

	const RepositoryView = () => (
		<Stack spacing={5} mt={5}>
			<HeadingMd text="Repository" />
			<TextBox
				title="Repository account"
				subTitle={
					environment?.repositoryDetails?.sourceControlIntegrationName
				}
			/>
			<TextBox
				title="Repository name"
				subTitle={environment?.repositoryDetails?.repository}
			/>
			{isEhEnv && (
				<TextBox
					title="Repository linked on"
					subTitle={`${getReadableDate(
						environment?.repositoryDetails?.repositoryLinkedAt,
					)} at ${getReadableTime(environment?.repositoryDetails?.repositoryLinkedAt ?? '')}`}
				/>
			)}
			<IconText
				text={autoDeploytxt}
				IconName={mdiSourceBranch}
				iconSize={5}
				iconColor="neutral"
			/>
			<Divider />
		</Stack>
	);

	const HostNamesView = () => (
		<Stack spacing={5} mt={5}>
			<HeadingMd text={hostNamesAndUrlstxt} />
			<TextBox title={hostNametxt} subTitle={host} key={hostNametxt} />
			<TextBox
				title={dashboardtxt}
				subTitle={dashboardUrl}
				key={dashboardtxt}
				isLink={true}
				iconSize={14}
				linkIcon={mdiOpenInNew}
				subTitleFontWeight="semibold"
				href={dashboardUrl}
			/>
			<TextBox
				subTitle=""
				title={authoringGraphQlEndpointtxt}
				key={authoringGraphQlEndpointtxt}
				linkIcon={mdiOpenInNew}
				tooltip={authoringGraphQlEndpointTooltip}
			>
				<Link
					isExternal
					href={authoringGraphQlEndpoint}
					boxShadow="none !important"
					fontWeight="semibold"
				>
					{t(launchIDEtxt)}
					<Icon path={mdiOpenInNew} ml={1} />
				</Link>
			</TextBox>
			<ApiTokenComponent host={host} />
			<EdgeToken />
		</Stack>
	);

	return (
		<>
			{isEhEnv && <EhView />}
			{!isEhEnv && <CmView />}
			<RepositoryView />
			{!isEhEnv && <HostNamesView />}
		</>
	);
};

function EnviromentDetailsController() {
	const { isLegacyEnvsRoute } = useGetEnvType();
	if (isLegacyEnvsRoute) {
		return <CombinedEnvironmentDetails />;
	}

	return <EnvironmentDetailsLatest />;
}

export { EnviromentDetailsController as EnvironmentDetails };
